import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import StarterKit from "@tiptap/starter-kit";
import { observer } from "mobx-react-lite";
import { useIntl } from "react-intl";
import { RichTextEditor, MenuControlsContainer, MenuSelectHeading, MenuDivider, MenuButtonBold, MenuButtonItalic, MenuButtonStrikethrough, MenuButtonOrderedList, MenuButtonBulletedList, MenuButtonHorizontalRule, RichTextEditorRef } from "mui-tiptap";
import { useEffect, useRef } from "react";

export interface NoteBoxProps {
    currentSection: number;
    note: string;
    onChangeNote: (note: string) => void;
    readOnly?: boolean;
    authorName?: string;
}

const NoteBox: React.FC<NoteBoxProps> = observer(({ currentSection, note, onChangeNote, readOnly, authorName }) => {
    const intl = useIntl();

    const rteRef = useRef<RichTextEditorRef>(null);

    useEffect(() => {
        rteRef.current?.editor?.commands.setContent(note);
    }, [currentSection]);

    const displayName = authorName || intl.formatMessage({ id: 'review.runner' });

    if (readOnly) {
        const hasContent = !!note && note !== '<p></p>' && note !== '';
        return (
            <Box p={2}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" display="block" mb={1}>
                    {intl.formatMessage({ id: 'review.notesByRunner' }, { name: displayName })}
                </Typography>
                {hasContent ? (
                    <Box
                        sx={{ fontSize: '0.875rem', lineHeight: 1.6, '& p': { margin: 0 }, '& h1,h2,h3': { margin: '4px 0' } }}
                        dangerouslySetInnerHTML={{ __html: note }}
                    />
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        {intl.formatMessage({ id: 'review.noNotesByRunner' }, { name: displayName })}
                    </Typography>
                )}
            </Box>
        );
    }

    return (
        <Box p={1}>
            <RichTextEditor
                ref={rteRef}
                onUpdate={({ editor }) => { onChangeNote(editor.getHTML()); }}
                extensions={[StarterKit]}
                renderControls={() => (
                    <MenuControlsContainer>
                        <MenuSelectHeading />
                        <MenuDivider />
                        <MenuButtonBold />
                        <MenuButtonItalic />
                        <MenuButtonStrikethrough />
                        <MenuDivider />
                        <MenuDivider />
                        <MenuButtonOrderedList />
                        <MenuButtonBulletedList />
                        <MenuDivider />
                        <MenuButtonHorizontalRule />
                    </MenuControlsContainer>
                )}
            />
        </Box>
    );
});

export default NoteBox;